import 'react-block-ui/style.css';

import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { Dialog, DialogType, IconButton } from '@fluentui/react';
import styles from './PatientFileDialog.module.scss';

import { PatientFileData } from '../../../redux/patient-files/types';
import { PatientFileDetails } from '../../patient/PatientFileDetails';
import { RootState } from '../../../redux/types';
import { Provider as ProviderContext, connect } from 'react-redux';
import { store } from '../../../redux/store';

export interface OwnProps {
  files?: PatientFileData[];
  hideDocumentType?: boolean;
  activeFileId: string;
  patientId: string;
  patientFileAction?: (actionName: 'Sending' | 'Deleting' | 'Updating' | 'Releasing', file: PatientFileData, description?: string) => void;
  updateActiveFile?: (activeFileIndex: number | null) => void;
  onClosed?: (success: boolean) => void;
}

export interface StateProps {
  isFetching: boolean;
}

type PatientFileDialogProps = OwnProps & StateProps;

export interface PatientFilesState {
  activeFileIndex?: number;
}

export class PatientFileDialog {
  public static show(options: OwnProps) {
    const dialogNode = document.createElement('div');
    const close = (): void => {
      ReactDOM.unmountComponentAtNode(dialogNode);
      dialogNode.remove();
    };
    ReactDOM.render(
      <ProviderContext store={store}>
        <PatientFileDialogInternal {...options} close={close} />
      </ProviderContext>,
      dialogNode
    );
  }
}

class Component extends React.Component<PatientFileDialogProps & { close: () => void }, PatientFilesState> {
  constructor(props: PatientFileDialogProps & { close: () => void }) {
    super(props);
    this.state = {};
  }

  public componentDidMount() {
    if (this.props.files) {
      const activeFileIndex = this.props.files.findIndex((file) => file._id === this.props.activeFileId);
      this.setState({ activeFileIndex });
    }
  }

  public componentDidUpdate(prevProps: PatientFileDialogProps) {
    if (prevProps.isFetching && !this.props.isFetching) {
      this.close();
    }
  }

  public render(): JSX.Element | null {
    const { isFetching, files, patientId, patientFileAction, hideDocumentType } = this.props;

    if (!files || this.state.activeFileIndex === undefined) {
      return null;
    }

    return (
      <Dialog hidden={false} dialogContentProps={{ type: DialogType.normal }} onDismiss={() => this.close()} minWidth={700}>
        {this.renderNavigation()}
        <PatientFileDetails
          file={files[this.state.activeFileIndex]}
          patientId={patientId}
          hideDocumentType={hideDocumentType}
          isSaving={isFetching}
          patientFileAction={patientFileAction}
          handleKeyPress={this.handleKeyPress}
          onClosed={this.close}
        />
      </Dialog>
    );
  }

  private renderNavigation(): JSX.Element | null {
    if (this.props.files?.length && this.props.files.length > 1) {
      return (
        <div className={styles.navigation}>
          <IconButton disabled={this.isFirstFile()} iconProps={{ iconName: 'ChevronLeftMed' }} onClick={() => this.renderFile(this.state.activeFileIndex! - 1)} />
          <div className={styles.navigationDetails}>
            Showing file {this.state.activeFileIndex! + 1} of {this.props.files.length}
          </div>
          <IconButton disabled={this.isLastFile()} iconProps={{ iconName: 'ChevronRightMed' }} onClick={() => this.renderFile(this.state.activeFileIndex! + 1)} />
        </div>
      );
    }
    return null;
  }

  private renderFile = (activeFileIndex: number) => {
    this.setState({ activeFileIndex });
    if (this.props.updateActiveFile) {
      this.props.updateActiveFile(activeFileIndex);
    }
  };

  private isFirstFile(): boolean {
    return this.state.activeFileIndex! === 0;
  }

  private isLastFile(): boolean {
    return this.state.activeFileIndex! + 1 === this.props.files!.length;
  }

  private handleKeyPress = (key: string) => {
    switch (key) {
      case 'ArrowRight':
        !this.isLastFile() && this.renderFile(this.state.activeFileIndex! + 1);
        break;
      case 'ArrowLeft':
        !this.isFirstFile() && this.renderFile(this.state.activeFileIndex! - 1);
        break;
      default:
        break;
    }
  };

  private close = (success = false) => {
    if (this.props.onClosed) {
      this.props.onClosed(success);
    }
    if (this.props.updateActiveFile) {
      this.props.updateActiveFile(null);
    }
    this.props.close();
  };
}

export function mapStateToProps(state: RootState, ownProps: OwnProps): StateProps {
  return {
    isFetching: state.patientFiles.data[ownProps.patientId].isFetching,
  };
}

export const PatientFileDialogInternal = connect<StateProps, undefined, OwnProps, RootState>(mapStateToProps)(Component);
